import {
  isSameScannerId,
  SCANNER_INTEGRATION_IDS,
} from "@/core/app/slices/integrations/scannerConstants"

import { closeOAuthPopupAfterRedirect } from "./scannerOAuthWindow"

// Unique per page-load — prevents cross-tab collisions when two tabs open the same scanner's OAuth.
const TAB_ID = (() => {
  try { return crypto.randomUUID() } catch { return `${Date.now()}-${Math.random().toString(36).slice(2)}` }
})()
export { TAB_ID as SCANNER_OAUTH_TAB_ID }

/** localStorage key used as a BroadcastChannel fallback for environments where it is unavailable. */
export const SCANNER_OAUTH_STORAGE_RESULT_KEY = "udc-scanner-oauth-result"

/** OAuth redirect path segment — register this with the API / IdP. */
export const INTEGRATIONS_OAUTH_CALLBACK_PATH = "/integrations/callback" as const

/**
 * Query keys for return URLs. EasyRx uses legacy `easyrx` only today; `scanner` + `oauth` are reserved
 * for a future multi-scanner redirect (see “Generic return URL” below).
 */
export const Q_SCANNER = "scanner"
export const Q_OAUTH = "oauth"
export const Q_LEGACY_EASYRX = "easyrx"

export const SCANNER_OAUTH_QUERY_SCANNER = Q_SCANNER
export const SCANNER_OAUTH_QUERY_OAUTH = Q_OAUTH
export const SCANNER_OAUTH_LEGACY_EASYRX = Q_LEGACY_EASYRX

// --- postMessage / BroadcastChannel ---

// One literal: payload `type` and `BroadcastChannel(name)` are different APIs but must not drift.
const SCANNER_OAUTH_PROTOCOL_ID = "udc-scanner-oauth" as const
export const MSG_SCANNER_OAUTH = SCANNER_OAUTH_PROTOCOL_ID
export const CH_SCANNER_OAUTH = SCANNER_OAUTH_PROTOCOL_ID
export const SCANNER_OAUTH_MESSAGE_TYPE = MSG_SCANNER_OAUTH
export const SCANNER_OAUTH_BROADCAST_CHANNEL = CH_SCANNER_OAUTH

/** Payload sent to the opener tab via `postMessage` or `BroadcastChannel` after OAuth completes. */
export type ScannerOAuthMessagePayload = {
  type: typeof MSG_SCANNER_OAUTH
  scanner: string
  ok: boolean
  tabId: string
}

// --- localStorage: written before `window.open`, read after IdP redirects ---

const LS_POPUP = "udc-scanner-oauth-popup"
const MARKER_TTL_MS = 15 * 60 * 1000

/** Reads the popup marker from localStorage, if present and valid JSON. */
function readPopupMarker(): { scanner: string; ts: number; tabId?: string } | null {
  try {
    const raw = localStorage.getItem(LS_POPUP)
    if (!raw) return null
    const o = JSON.parse(raw) as { scanner?: string; ts?: number; tabId?: string }
    if (typeof o.scanner !== "string" || typeof o.ts !== "number") return null
    return { scanner: o.scanner, ts: o.ts, tabId: o.tabId }
  } catch {
    return null
  }
}

/**
 * Records that the user started an OAuth popup for this scanner (call before `window.open`).
 * Used after redirect when `window.opener` may be missing so we can still treat the window as our popup.
 */
export function markScannerOAuthPopupOpened(scannerId: string): void {
  try {
    localStorage.setItem(
      LS_POPUP,
      JSON.stringify({ scanner: scannerId, ts: Date.now(), tabId: TAB_ID }),
    )
  } catch {
    /* storage disabled */
  }
}

/** Removes the OAuth-popup marker from localStorage (e.g. after handling the return or on disconnect). */
export function clearScannerOAuthPopupMarker(): void {
  try {
    localStorage.removeItem(LS_POPUP)
  } catch {
    /* ignore */
  }
}

/**
 * Whether a non-expired marker exists for this scanner (same-origin hint only — not an auth check).
 */
export function hasRecentScannerOAuthPopupMarker(scannerId: string): boolean {
  const m = readPopupMarker()
  if (!m || !isSameScannerId(m.scanner, scannerId)) return false
  if (Date.now() - m.ts > MARKER_TTL_MS) {
    clearScannerOAuthPopupMarker()
    return false
  }
  return true
}

/**
 * Stable `window.open` target name for this scanner; survives many OAuth redirects so the return page can
 * detect the popup via `window.name` when `opener` is null.
 */
export function scannerOAuthWindowName(scannerId: string): string {
  return `UdcOAuth_${scannerId.replace(/[^a-zA-Z0-9_-]/g, "_")}`
}

// --- Return URL query parsing ---

export type ParsedScannerOAuth = { scanner: string; ok: boolean } | null

/*
 * Generic return URL (not implemented — add when backend/IdP use `scanner` + `oauth`):
 *
 * 1. Redirect: `…/integrations/callback?scanner=<id>&oauth=<result>` (`<result>` → boolean, e.g. map
 *    success/true/1 vs failure/false/0 in a small helper).
 * 2. In `parseScannerOAuthQuery`, before the EasyRx legacy branch: if `trim(get(Q_SCANNER))` and
 *    parsed `get(Q_OAUTH)` are both valid, return `{ scanner, ok }`.
 * 3. Register ids in `SCANNER_INTEGRATION_OAUTH_IDS` (`scannerConstants.ts`) for the callback route.
 * 4. `stripScannerOAuthParams` already deletes `scanner`, `oauth`, and `easyrx`.
 */

/**
 * Parses the current URL’s OAuth return query params into `{ scanner, ok }`, or `null` if this is not an OAuth return.
 * EasyRx only today: `?easyrx=true|false`. See block comment above for multi-scanner rollout.
 */
export function parseScannerOAuthQuery(
  searchParams: URLSearchParams,
): ParsedScannerOAuth {
  const legacy = searchParams.get(Q_LEGACY_EASYRX)
  if (legacy === "true") {
    return { scanner: SCANNER_INTEGRATION_IDS.EASYRX, ok: true }
  }
  if (legacy === "false") {
    return { scanner: SCANNER_INTEGRATION_IDS.EASYRX, ok: false }
  }
  return null
}

/**
 * Removes OAuth-related query keys from the address bar via `history.replaceState` (no full page reload).
 */
export function stripScannerOAuthParams(searchParams: URLSearchParams): void {
  const next = new URLSearchParams(searchParams)
  next.delete(Q_SCANNER)
  next.delete(Q_OAUTH)
  next.delete(Q_LEGACY_EASYRX)
  const qs = next.toString()
  const path = window.location.pathname
  window.history.replaceState({}, "", qs ? `${path}?${qs}` : path)
}

// --- Notify opener and close popup ---

/**
 * Notifies the parent tab of success/failure (`postMessage` or `BroadcastChannel`), clears the popup marker,
 * and schedules closing this window. Returns `true` if this tab was treated as our OAuth popup.
 */
export function notifyOpenerAndCloseScanner(
  scannerId: string,
  ok: boolean,
): boolean {
  const tabId = readPopupMarker()?.tabId ?? ""
  const payload: ScannerOAuthMessagePayload = {
    type: MSG_SCANNER_OAUTH,
    scanner: scannerId,
    ok,
    tabId,
  }

  const hasOpener = !!(window.opener && !window.opener.closed)
  const namedPopup = window.name === scannerOAuthWindowName(scannerId)
  const marked = hasRecentScannerOAuthPopupMarker(scannerId)
  const isOurPopup = hasOpener || namedPopup || marked

  if (hasOpener) {
    window.opener!.postMessage(payload, window.location.origin)
  } else if (isOurPopup) {
    try {
      const ch = new BroadcastChannel(CH_SCANNER_OAUTH)
      ch.postMessage(payload)
      ch.close()
    } catch {
      /* BroadcastChannel unsupported */
    }
  }

  // localStorage storage-event fallback — fires in the opener tab even if BroadcastChannel
  // is unavailable (e.g. Safari private browsing < 15.4).
  try {
    localStorage.setItem(SCANNER_OAUTH_STORAGE_RESULT_KEY, JSON.stringify(payload))
    window.setTimeout(() => {
      try { localStorage.removeItem(SCANNER_OAUTH_STORAGE_RESULT_KEY) } catch { /* ignore */ }
    }, 5_000)
  } catch { /* storage disabled */ }

  if (!isOurPopup) return false

  clearScannerOAuthPopupMarker()
  // Defer so the opener can handle `postMessage` before the popup unloads.
  window.setTimeout(() => closeOAuthPopupAfterRedirect(), 0)
  return true
}

/** Type guard for `message` / `BroadcastChannel` data — use before reading `scanner` or `ok`. */
export function isScannerOAuthMessage(
  data: unknown,
): data is ScannerOAuthMessagePayload {
  if (data == null || typeof data !== "object") return false
  const d = data as Record<string, unknown>
  return (
    d.type === MSG_SCANNER_OAUTH &&
    typeof d.scanner === "string" &&
    typeof d.ok === "boolean" &&
    typeof d.tabId === "string"
  )
}
